import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { formatFirestoreTimestamp } from "../../../../../utils/formatTimestampToDate";

const CompletedBetSummaryModal = ({
  open,
  onClose,
  betSummary,
}) => {
  const [tab, setTab] = useState(0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          minHeight: "75vh",
          maxWidth:"1000px!important"
        },
      }}
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
        Completed Bet Summary
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!betSummary ? (
          <Typography>No data found.</Typography>
        ) : (
          <Stack spacing={3}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 3,
                background:
                  "linear-gradient(180deg, rgba(255,90,61,0.07) 0%, rgba(255,90,61,0.02) 100%)",
                border: "1px solid",
                borderColor: alpha("#ff5a3d", 0.18),
              }}
            >
              <Typography variant="h6" fontWeight={800} mb={2}>
                Bet Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography><strong>Name:</strong> {betSummary.bet?.betName || "-"}</Typography>
                  <Typography><strong>Status:</strong> {betSummary.bet?.betStatus || "-"}</Typography>
                  <Typography><strong>Challenge Type:</strong> {betSummary.bet?.challengeType || "-"}</Typography>
                  <Typography><strong>Bet Amount:</strong> {betSummary.bet?.betAmount ?? "-"}</Typography>
                  <Typography><strong>Bet Pool:</strong> {betSummary.bet?.betPool ?? "-"}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ wordBreak: "break-all" }}>
                    <strong>Bet ID:</strong> {betSummary.bet?.betID || "-"}
                  </Typography>
                  <Typography>
                    <strong>Accepted Participants:</strong> {betSummary.acceptedParticipants?.length ?? 0}
                  </Typography>
                  <Typography>
                    <strong>Winners:</strong> {betSummary.winners?.length ?? 0}
                  </Typography>
                  <Typography>
                    <strong>Completion Date:</strong>{" "}
                    {betSummary.bet?.completionDate
                      ? formatFirestoreTimestamp(betSummary.bet.completionDate)
                      : "-"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6} md={2}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha("#2196f3", 0.08),
                    border: "1px solid",
                    borderColor: alpha("#2196f3", 0.18),
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Total Records
                  </Typography>
                  <Typography variant="h6" fontWeight={800}>
                    {betSummary.ledgerSummary?.totalRecords ?? 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} md={2}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha("#ff9800", 0.08),
                    border: "1px solid",
                    borderColor: alpha("#ff9800", 0.18),
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Bet Place
                  </Typography>
                  <Typography variant="h6" fontWeight={800}>
                    {betSummary.ledgerSummary?.betPlaceCount ?? 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} md={2}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha("#4caf50", 0.08),
                    border: "1px solid",
                    borderColor: alpha("#4caf50", 0.18),
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Release Stake
                  </Typography>
                  <Typography variant="h6" fontWeight={800}>
                    {betSummary.ledgerSummary?.releaseStakeCount ?? 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} md={2}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha("#9c27b0", 0.08),
                    border: "1px solid",
                    borderColor: alpha("#9c27b0", 0.18),
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Transfer Out
                  </Typography>
                  <Typography variant="h6" fontWeight={800}>
                    {betSummary.ledgerSummary?.transferOutCount ?? 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} md={2}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha("#673ab7", 0.08),
                    border: "1px solid",
                    borderColor: alpha("#673ab7", 0.18),
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Transfer In
                  </Typography>
                  <Typography variant="h6" fontWeight={800}>
                    {betSummary.ledgerSummary?.transferInCount ?? 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} md={2}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha("#f44336", 0.08),
                    border: "1px solid",
                    borderColor: alpha("#f44336", 0.18),
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Platform Fee
                  </Typography>
                  <Typography variant="h6" fontWeight={800}>
                    {betSummary.ledgerSummary?.platformFeeCount ?? 0}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider />

            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label={`Winners (${betSummary.bet?.winners?.length || 0})`} />
              <Tab label={`Accepted Participants (${betSummary.acceptedParticipants?.length || 0})`} />
              <Tab label={`Ledger Records (${betSummary.ledgerRecords?.length || 0})`} />
            </Tabs>

            {tab === 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Winner ID</TableCell>
                    <TableCell>Won Amount</TableCell>
                    <TableCell>After Fee</TableCell>
                    <TableCell>Fee</TableCell>
                    <TableCell>Fee Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(betSummary.bet?.winners || []).map((winner, index) => (
                    <TableRow key={index}>
                      <TableCell>{winner.winnerID}</TableCell>
                      <TableCell>{winner.wonAmount ?? "-"}</TableCell>
                      <TableCell>{winner.wonAmountAfterFee ?? "-"}</TableCell>
                      <TableCell>{winner.platformFeeAmount ?? "-"}</TableCell>
                      <TableCell>{winner.platformFeeRate ?? "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {tab === 1 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Winner</TableCell>
                    <TableCell>Won Amount</TableCell>
                    <TableCell>Accept Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(betSummary.acceptedParticipants || []).map((participant) => (
                    <TableRow key={participant.id}>
                      <TableCell>{participant.userID}</TableCell>
                      <TableCell>
                        <Chip
                          label={participant.inviteStatus || "-"}
                          size="small"
                          color={participant.inviteStatus === "Accepted" ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>{participant.isWinner ? "Yes" : "No"}</TableCell>
                      <TableCell>{participant.wonAmount ?? "-"}</TableCell>
                      <TableCell>
                        {participant.acceptDate
                          ? formatFirestoreTimestamp(participant.acceptDate)
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {tab === 2 && (
              <Box sx={{ maxHeight: 430, overflowY: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>User ID</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(betSummary.ledgerRecords || []).map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{record.type}</TableCell>
                        <TableCell>{record.userId}</TableCell>
                        <TableCell>{record.amount}</TableCell>
                        <TableCell>{record.description}</TableCell>
                        <TableCell>
                          {record.createdDate
                            ? formatFirestoreTimestamp(record.createdDate)
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: "999px",
            px: 3,
            background: "linear-gradient(135deg, #ff6a4d 0%, #ff4b2b 100%)",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompletedBetSummaryModal;